import { useEffect, useMemo, useReducer, useRef } from "react";
import {
  useLazyGetCompanyLogosByMetaRefsQuery,
} from "../../../services/pub";
import { useLazyGetPeopleListIcpsQuery } from "../../../services/searches";
import type { IExecProjectListItem } from "../../../types/agent-pipelines";
import type { IGetCompanyLogosResponseDataObj } from "../../../types/pub";
import type { IPeopleListIcpsResponse } from "../../../types/searchRequest";

const PEOPLE_LIST_ICP_BATCH_SIZE = 10;
const COMPANY_META_REF_BATCH_SIZE = 50;
const VISIBLE_ENRICHMENT_DEBOUNCE_MS = 50;
const selectNoQueryState = () => ({});

const chunkValues = <T,>(values: T[], size: number): T[][] => {
  const chunks: T[][] = [];

  for (let index = 0; index < values.length; index += size) {
    chunks.push(values.slice(index, index + size));
  }

  return chunks;
};

const getVisibleEnrichmentIds = ({
  includeClientDetails,
  projects,
}: {
  includeClientDetails: boolean;
  projects: IExecProjectListItem[];
}) => {
  const companyMetaRefs = new Set<string>();
  const peopleListIcpIds = new Set<string>();

  for (const project of projects) {
    const peopleListIcpId = project.people_list_icp_id?.trim();
    if (peopleListIcpId) peopleListIcpIds.add(peopleListIcpId);

    if (includeClientDetails && project.client_company_id) {
      companyMetaRefs.add(project.client_company_id);
    }
    if (project.hiring_company_id) {
      companyMetaRefs.add(project.hiring_company_id);
    }
  }

  return {
    companyMetaRefs: Array.from(companyMetaRefs),
    peopleListIcpIds: Array.from(peopleListIcpIds),
  };
};

interface IVisibleProjectEnrichmentState {
  companyDetailsByMetaRef: Record<
    string,
    IGetCompanyLogosResponseDataObj | undefined
  >;
  peopleListIcps: IPeopleListIcpsResponse;
  scopeKey: string;
  settledPeopleListIcpIds: Set<string>;
}

type VisibleProjectEnrichmentAction =
  | {
      companyDetails: IGetCompanyLogosResponseDataObj[];
      scopeKey: string;
      type: "merge_companies";
    }
  | {
      peopleListIcpIds: string[];
      peopleListIcps?: IPeopleListIcpsResponse;
      scopeKey: string;
      type: "settle_people_list_icps";
    }
  | { scopeKey: string; type: "reset" };

const createVisibleProjectEnrichmentState = (
  scopeKey: string,
): IVisibleProjectEnrichmentState => ({
  companyDetailsByMetaRef: {},
  peopleListIcps: {},
  scopeKey,
  settledPeopleListIcpIds: new Set<string>(),
});

const visibleProjectEnrichmentReducer = (
  state: IVisibleProjectEnrichmentState,
  action: VisibleProjectEnrichmentAction,
): IVisibleProjectEnrichmentState => {
  if (action.type === "reset") {
    return createVisibleProjectEnrichmentState(action.scopeKey);
  }
  if (state.scopeKey !== action.scopeKey) return state;

  if (action.type === "merge_companies") {
    const companyDetailsByMetaRef = { ...state.companyDetailsByMetaRef };

    for (const companyDetails of action.companyDetails) {
      const metaRef = companyDetails?.company?.meta_ref;
      if (metaRef) companyDetailsByMetaRef[metaRef] = companyDetails;
    }

    return { ...state, companyDetailsByMetaRef };
  }

  const settledPeopleListIcpIds = new Set(state.settledPeopleListIcpIds);
  action.peopleListIcpIds.forEach((icpId) =>
    settledPeopleListIcpIds.add(icpId),
  );

  return {
    ...state,
    peopleListIcps: action.peopleListIcps
      ? { ...state.peopleListIcps, ...action.peopleListIcps }
      : state.peopleListIcps,
    settledPeopleListIcpIds,
  };
};

interface IRequestedEnrichmentIds {
  companyMetaRefs: Set<string>;
  peopleListIcpIds: Set<string>;
  scopeKey: string;
}

const createRequestedEnrichmentIds = (
  scopeKey: string,
): IRequestedEnrichmentIds => ({
  companyMetaRefs: new Set<string>(),
  peopleListIcpIds: new Set<string>(),
  scopeKey,
});

export const useVisibleProjectEnrichment = ({
  enabled,
  includeClientDetails,
  joid,
  projects,
  scopeKey: pageScopeKey = "default",
}: {
  enabled: boolean;
  includeClientDetails: boolean;
  joid?: string;
  projects: IExecProjectListItem[];
  scopeKey?: string;
}) => {
  const scopeKey = `${joid || ""}:${pageScopeKey}`;
  const [getPeopleListIcps] = useLazyGetPeopleListIcpsQuery({
    selectFromResult: selectNoQueryState,
  });
  const [getCompanyLogosByMetaRefs] =
    useLazyGetCompanyLogosByMetaRefsQuery({
      selectFromResult: selectNoQueryState,
    });
  const [state, dispatch] = useReducer(
    visibleProjectEnrichmentReducer,
    scopeKey,
    createVisibleProjectEnrichmentState,
  );
  const requestedIds = useRef(createRequestedEnrichmentIds(scopeKey));
  const { companyMetaRefs, peopleListIcpIds } = useMemo(
    () => getVisibleEnrichmentIds({ includeClientDetails, projects }),
    [includeClientDetails, projects],
  );

  useEffect(() => {
    requestedIds.current = createRequestedEnrichmentIds(scopeKey);
    dispatch({ scopeKey, type: "reset" });
  }, [scopeKey]);

  useEffect(() => {
    if (!enabled || !joid || state.scopeKey !== scopeKey) return;

    const timeoutId = window.setTimeout(() => {
      const currentRequestedIds = requestedIds.current;
      if (currentRequestedIds.scopeKey !== scopeKey) return;

      const newPeopleListIcpIds = peopleListIcpIds.filter(
        (icpId) => !currentRequestedIds.peopleListIcpIds.has(icpId),
      );
      const newCompanyMetaRefs = companyMetaRefs.filter(
        (metaRef) => !currentRequestedIds.companyMetaRefs.has(metaRef),
      );

      newPeopleListIcpIds.forEach((icpId) =>
        currentRequestedIds.peopleListIcpIds.add(icpId),
      );
      newCompanyMetaRefs.forEach((metaRef) =>
        currentRequestedIds.companyMetaRefs.add(metaRef),
      );

      for (const icpIds of chunkValues(
        newPeopleListIcpIds,
        PEOPLE_LIST_ICP_BATCH_SIZE,
      )) {
        void getPeopleListIcps({ icpIds, joid }, true)
          .unwrap()
          .then((peopleListIcps) => {
            dispatch({
              peopleListIcpIds: icpIds,
              peopleListIcps,
              scopeKey,
              type: "settle_people_list_icps",
            });
          })
          .catch(() => {
            dispatch({
              peopleListIcpIds: icpIds,
              scopeKey,
              type: "settle_people_list_icps",
            });
          });
      }

      for (const metaRefs of chunkValues(
        newCompanyMetaRefs,
        COMPANY_META_REF_BATCH_SIZE,
      )) {
        void getCompanyLogosByMetaRefs({ joid, metaRefs }, true)
          .unwrap()
          .then((response) => {
            dispatch({
              companyDetails: response.data || [],
              scopeKey,
              type: "merge_companies",
            });
          })
          .catch(() => undefined);
      }
    }, VISIBLE_ENRICHMENT_DEBOUNCE_MS);

    return () => window.clearTimeout(timeoutId);
  }, [
    companyMetaRefs,
    enabled,
    getCompanyLogosByMetaRefs,
    getPeopleListIcps,
    joid,
    peopleListIcpIds,
    scopeKey,
    state.scopeKey,
  ]);

  if (state.scopeKey !== scopeKey) {
    return createVisibleProjectEnrichmentState(scopeKey);
  }

  return state;
};
